import React, { useState, useMemo } from 'react';
import { FiArrowUp, FiArrowDown } from 'react-icons/fi';
import { IconType } from 'react-icons';
import { AppMetricsResponse } from '../../api/types';

interface AppMetricsTableProps {
  appMetricsData: AppMetricsResponse[];
  loading: boolean;
  /** Retention tablosuyla aynı seçim; ülke metni buradan üretilir. */
  retentionCountries: string[];
}

const AppMetricsTable: React.FC<AppMetricsTableProps> = ({
  appMetricsData,
  loading,
  retentionCountries,
}) => {
  const [sortColumn, setSortColumn] = useState<string | null>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const renderIcon = (Icon: IconType, className: string) => {
    const IconComponent = Icon as React.ComponentType<{ className?: string }>;
    return <IconComponent className={className} />;
  };

  // Format duration in seconds to readable format
  const formatDuration = (seconds: number | null | undefined): string => {
    if (seconds === null || seconds === undefined) return '-';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  const transformedAppMetricsData = appMetricsData.map((row) => ({
    date: row.target_date || '-',
    signupCount: row.signup_count ?? null,
    dailyCheckins: row.daily_checkins ?? null,
    avgAppDuration: row.avg_app_duration ?? null,
    avgGameDuration: row.avg_game_duration ?? null,
    avgTestDuration: row.avg_test_duration ?? null,
  }));

  // Handle sort
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortColumn) return transformedAppMetricsData;

    return [...transformedAppMetricsData].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case 'date':
          aValue = new Date(a.date).getTime();
          bValue = new Date(b.date).getTime();
          break;
        case 'signupCount':
          aValue = a.signupCount ?? 0;
          bValue = b.signupCount ?? 0;
          break;
        case 'dailyCheckins':
          aValue = a.dailyCheckins ?? 0;
          bValue = b.dailyCheckins ?? 0;
          break;
        case 'avgAppDuration':
          aValue = a.avgAppDuration ?? 0;
          bValue = b.avgAppDuration ?? 0;
          break;
        case 'avgGameDuration':
          aValue = a.avgGameDuration ?? 0;
          bValue = b.avgGameDuration ?? 0;
          break;
        case 'avgTestDuration':
          aValue = a.avgTestDuration ?? 0;
          bValue = b.avgTestDuration ?? 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [transformedAppMetricsData, sortColumn, sortDirection]);

  const countryLine = retentionCountries[0] ?? 'Tüm Ülkeler';

  return (
    <div className="glass-strong rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 p-6 border transform hover:scale-[1.01] animate-fade-in">
      <div className="mb-4">
        <div>
          <h3 className="text-lg font-bold gradient-text mb-1">Uygulama Metrikleri Panosu</h3>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            30 günlük günlük uygulama kullanım metrikleri
            <span className="text-slate-500 dark:text-slate-500"> · </span>
            <span className="font-medium text-slate-700 dark:text-slate-300">{countryLine}</span>
          </p>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gradient-to-r from-slate-50 to-gray-50 dark:from-gray-700 dark:to-gray-800">
              <th 
                className="px-4 py-3 text-left font-semibold text-slate-600 dark:text-slate-300 border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                onClick={() => handleSort('date')}
              >
                <div className="flex items-center gap-2">
                  <span>TARİH</span>
                  {sortColumn === 'date' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left font-semibold text-slate-600 dark:text-slate-300 border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                onClick={() => handleSort('signupCount')}
              >
                <div className="flex items-center gap-2">
                  <span>GÜNLÜK KAYITLAR</span>
                  {sortColumn === 'signupCount' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left font-semibold text-slate-600 dark:text-slate-300 border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                onClick={() => handleSort('dailyCheckins')}
              >
                <div className="flex items-center gap-2">
                  <span>GÜNLÜK GİRİŞLER</span>
                  {sortColumn === 'dailyCheckins' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left font-semibold text-slate-600 dark:text-slate-300 border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                onClick={() => handleSort('avgAppDuration')}
              >
                <div className="flex items-center gap-2">
                  <span>ORT. UYGULAMA SÜRESİ</span>
                  {sortColumn === 'avgAppDuration' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left font-semibold text-slate-600 dark:text-slate-300 border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                onClick={() => handleSort('avgGameDuration')}
              >
                <div className="flex items-center gap-2">
                  <span>ORT. OYUN SÜRESİ</span>
                  {sortColumn === 'avgGameDuration' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left font-semibold text-slate-600 dark:text-slate-300 border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                onClick={() => handleSort('avgTestDuration')}
              >
                <div className="flex items-center gap-2">
                  <span>ORT. TEST SÜRESİ</span>
                  {sortColumn === 'avgTestDuration' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-slate-500 dark:text-slate-400">
                  {loading ? 'Yükleniyor...' : 'App metrics verisi bulunamadı'}
                </td>
              </tr>
            ) : (
              sortedData.map((row, index) => (
                <tr 
                  key={index} 
                  className="hover:bg-gradient-to-r hover:from-indigo-50/50 hover:to-purple-50/50 dark:hover:from-indigo-900/20 dark:hover:to-purple-900/20 transition-all duration-300 border-b border-gray-100/50 dark:border-gray-700/50"
                >
                  <td className="px-4 py-3 font-medium text-slate-700 dark:text-slate-300 border border-gray-200 dark:border-gray-600">
                    {row.date}
                  </td>
                  <td className="px-4 py-3 border border-gray-200 dark:border-gray-600">
                    {row.signupCount !== null && row.signupCount !== undefined ? (
                      <span className="inline-block bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900 dark:to-emerald-900 text-green-800 dark:text-green-200 px-3 py-1 rounded-lg font-semibold shadow-sm">
                        {row.signupCount}
                      </span>
                    ) : (
                      <span className="text-slate-400 dark:text-slate-500">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 border border-gray-200 dark:border-gray-600">
                    {row.dailyCheckins !== null && row.dailyCheckins !== undefined ? (
                      <span className="inline-block bg-gradient-to-r from-cyan-100 to-blue-100 dark:from-cyan-900 dark:to-blue-900 text-cyan-800 dark:text-cyan-200 px-3 py-1 rounded-lg font-semibold shadow-sm">
                        {row.dailyCheckins}
                      </span>
                    ) : (
                      <span className="text-slate-400 dark:text-slate-500">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400 border border-gray-200 dark:border-gray-600">
                    {row.avgAppDuration !== null && row.avgAppDuration !== undefined ? (
                      <span className="inline-block bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900 dark:to-indigo-900 text-blue-800 dark:text-blue-200 px-3 py-1 rounded-lg font-medium">
                        {formatDuration(row.avgAppDuration)}
                      </span>
                    ) : (
                      <span className="text-slate-400 dark:text-slate-500">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400 border border-gray-200 dark:border-gray-600">
                    {row.avgGameDuration !== null && row.avgGameDuration !== undefined ? (
                      <span className="inline-block bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900 dark:to-pink-900 text-purple-800 dark:text-purple-200 px-3 py-1 rounded-lg font-medium">
                        {formatDuration(row.avgGameDuration)}
                      </span>
                    ) : (
                      <span className="text-slate-400 dark:text-slate-500">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400 border border-gray-200 dark:border-gray-600">
                    {row.avgTestDuration !== null && row.avgTestDuration !== undefined ? (
                      <span className="inline-block bg-gradient-to-r from-orange-100 to-amber-100 dark:from-orange-900 dark:to-amber-900 text-orange-800 dark:text-orange-200 px-3 py-1 rounded-lg font-medium">
                        {formatDuration(row.avgTestDuration)}
                      </span>
                    ) : (
                      <span className="text-slate-400 dark:text-slate-500">-</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AppMetricsTable;

